<template>
  <div>
    <v-row
      class="mt-0"
      no-gutters
    >
      <v-spacer />
      <v-col cols="4">
        <v-select
          v-model="diagnostico"
          :items="diagnosticos"
          item-text="nome"
          item-value="id"
          label="Diagnóstico:"
          return-object
        />
      </v-col>
    </v-row>
    <v-card-widget title="Dashboard">
      <div
        v-if="diagnosticos.length === 0"
        slot="widget-content"
      >
        <app-alert
          type="info"
          message="Não há diagnósticos disponíveis."
        />
      </div>
      <div
        v-else-if="!diagnostico"
        slot="widget-content"
      >
        <app-alert
          type="info"
          message="Selecione um diagnóstico."
        />
      </div>
      <div
        v-else-if="isDispensado"
        slot="widget-content"
      >
        <info-dispensa :mensagem="mensagemDispensa" />
      </div>
      <div
        v-else
        slot="widget-content"
      >
        <app-timeline :itens="timelineItens" />
      </div>
    </v-card-widget>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VCardWidget from '@/components/VWidget';
import AppAlert from '@/components/core/AppAlert';
import InfoDispensa from '@/components/diagnostico/InfoDispensa';
import AppTimeline from '@/components/core/AppTimeline';

export default {
  name: 'DashboardDiagnostico',

  components: {
    VCardWidget,
    InfoDispensa,
    AppAlert,
    AppTimeline,
  },

  computed: {
    ...mapState({
      comissao: (state) => state.auth.comissao,
      pares: (state) => state.mapeamento.usuarioMapeamento.pares,
      dispensas: (state) => state.diagnostico.dispensas,
      isDispensado: (state) => state.diagnostico.dispensado,
      motivoDispensa: (state) => state.diagnostico.motivoDispensa,
      mapeamento: (state) => state.mapeamento.mapeamento,
      mapeamentos: (state) => state.mapeamento.mapeamentos,
      usuarioMapeamento: (state) => state.mapeamento.usuarioMapeamento,
      chefia: (state) => state.mapeamento.usuarioMapeamento.chefia,
      avaliaSubordinados: (state) => state.mapeamento.usuarioMapeamento.avaliaSubordinados,
      unidade: (state) => state.mapeamento.usuarioMapeamento.unidade,
      unidadePai: (state) => state.mapeamento.usuarioMapeamento.unidade.unidadePai,
    }),
    diagnostico: {
      get() {
        return this.$store.state.diagnostico.diagnostico;
      },
      set(diagnostico) {
        this.$store.dispatch('diagnostico/setDiagnostico', diagnostico);
      },
    },
    diagnosticos() {
      return this.$store.state.diagnostico.diagnosticos;
    },
    avaliacaoChefia() {
      return !(this.chefia && this.usuarioMapeamento.unidade.idVersao === this.mapeamento.unidade.idVersao)
        && ((!this.chefia && this.unidade.gestor != null)
        || (this.chefia && this.unidadePai.gestor != null));
    },
    mensagemDispensa() {
      return `A participação no diagnóstico não está disponível. (${this.motivoDispensa})`;
    },
    timelineItens() {
      return [
        {
          visibilidade: this.usuarioMapeamento.tipo === 'REGULAR',
          titulo: 'Autoavaliação (Competências Comportamentais)',
          textos: [
            'Na autoavaliação comportamental, você deve indicar a frequência com que apresentou, durante os '
              + 'últimos 12 meses, cada comportamento mapeado com sua contribuição na primeira etapa.',
            'Essa auto avaliação vai nos ajudar a direcionar a sua trilha de desenvolvimento.',
          ],
          cor: 'success',
          icone: 'person',
          link: '/diagnostico/comportamento/autoavaliacao',
          dataInicial: this.diagnostico.inicioComportamental,
          dataFinal: this.diagnostico.fimComportamental,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('AUTOAVALIACAO', 'COMPORTAMENTAL'),
          aviso: this.getAviso('AUTOAVALIACAO', 'COMPORTAMENTAL'),
        },
        {
          visibilidade: this.avaliacaoChefia && this.usuarioMapeamento.tipo === 'REGULAR',
          titulo: 'Avaliação do gestor imediato (Competências Comportamentais)',
          textos: [
            'Na avaliação comportamental do gestor imediato, você deve indicar a frequência com que o seu '
              + 'gestor apresentou, durante os últimos 12 meses, cada comportamento mapeado com sua '
              + 'contribuição na primeira etapa.',
            'Essa avaliação vai nos ajudar a direcionar a trilha de desenvolvimento da sua chefia imediata e '
              + 'melhorar o desempenho da sua unidade.',
          ],
          cor: 'success',
          icone: 'work',
          link: '/diagnostico/comportamento/chefia',
          dataInicial: this.diagnostico.inicioComportamental,
          dataFinal: this.diagnostico.fimComportamental,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('CHEFIA', 'COMPORTAMENTAL'),
          aviso: this.getAviso('CHEFIA', 'COMPORTAMENTAL'),
        },
        {
          visibilidade: this.avaliaSubordinados,
          titulo: 'Avaliação da equipe (Competências Comportamentais)',
          textos: [
            'Na avaliação comportamental dos membros da equipe da sua unidade, você deve indicar a frequência '
              + 'com que cada membro apresentou, durante os últimos 12 meses, cada comportamento mapeado '
              + 'com sua contribuição na primeira etapa.',
            'Essa avaliação vai nos ajudar a direcionar a trilha de desenvolvimento da equipe e melhorar '
              + 'o desempenho da sua unidade.',
          ],
          cor: 'success',
          icone: 'group',
          link: '/diagnostico/comportamento/servidores',
          dataInicial: this.diagnostico.inicioComportamental,
          dataFinal: this.diagnostico.fimComportamental,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('SUBORDINADO', 'COMPORTAMENTAL'),
          aviso: this.getAviso('SUBORDINADO', 'COMPORTAMENTAL'),
        },
        {
          visibilidade: !this.chefia && this.pares && this.usuarioMapeamento.tipo === 'REGULAR',
          titulo: 'Avaliação da equipe (Competências Comportamentais)',
          textos: [
            'Na avaliação comportamental dos membros da equipe (pares) da sua unidade, você deve indicar a '
              + 'frequência com que cada membro apresentou, durante os últimos 12 meses, cada comportamento '
              + 'mapeado com sua contribuição na primeira etapa.',
            'Essa avaliação vai nos ajudar a direcionar a trilha de desenvolvimento da equipe e melhorar o '
              + 'desempenho da sua unidade.',
          ],
          cor: 'success',
          icone: 'sync_alt',
          link: '/diagnostico/comportamento/pares',
          dataInicial: this.diagnostico.inicioComportamental,
          dataFinal: this.diagnostico.fimComportamental,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('PARES', 'COMPORTAMENTAL'),
          aviso: this.getAviso('PARES', 'COMPORTAMENTAL'),
        },
        {
          visibilidade: this.avaliaSubordinados,
          titulo: 'Avaliação da Equipe (Competências Técnicas)',
          textos: [
            'Na avaliação das competências técnicas da sua equipe, você deve indicar a forma com que cada '
              + 'membro realizou, durante os últimos 12 meses, as responsabilidades mapeadas na sua unidade.',
            'Essa avaliação vai nos ajudar a direcionar a trilha de desenvolvimento da equipe e melhorar o '
            + 'desempenho da sua unidade.',
          ],
          cor: 'secondary',
          icone: 'group',
          link: '/diagnostico/responsabilidade/servidores',
          dataInicial: this.diagnostico.inicioResponsabilidadeChefia,
          dataFinal: this.diagnostico.fimResponsabilidadeChefia,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('SUBORDINADO', 'RESPONSABILIDADE'),
          aviso: this.getAviso('SUBORDINADO', 'RESPONSABILIDADE'),
        },
        {
          visibilidade: this.usuarioMapeamento.tipo === 'REGULAR',
          titulo: 'Autoavaliação (Competências Técnicas)',
          textos: [
            'Na autoavaliação das competências técnicas, você deve indicar a forma com que realizou, durante '
              + 'os últimos 12 meses, as responsabilidades mapeadas em sua unidade.',
            'Essa autoavaliação vai nos ajudar a direcionar a sua trilha de desenvolvimento.',
          ],
          cor: 'secondary',
          icone: 'person',
          link: '/diagnostico/responsabilidade/autoavaliacao',
          dataInicial: this.diagnostico.inicioResponsabilidadeServidor,
          dataFinal: this.diagnostico.fimResponsabilidadeServidor,
          exibeStatus: true,
          progresso: this.$store.getters['diagnostico/getProgressoAvaliacao']('AUTOAVALIACAO', 'RESPONSABILIDADE'),
          aviso: this.getAviso('AUTOAVALIACAO', 'RESPONSABILIDADE'),
        },
      ];
    },
  },

  watch: {
    diagnostico() {
      this.loadAvaliacoes();
    },
  },

  created() {
    this.loadAvaliacoes();
  },

  methods: {
    loadAvaliacoes() {
      if (this.diagnostico) {
        this.$store.dispatch('diagnostico/loadAvaliacoes', this.diagnostico.id);
      }
    },
    getAviso(tipo, perspectiva) {
      const avaliacaoProp = tipo.toLowerCase() + perspectiva[0].toUpperCase() + perspectiva.substring(1).toLowerCase();

      if (this.$store.state.diagnostico[avaliacaoProp].length === 0) {
        return null;
      }

      return this.$store.state.diagnostico[avaliacaoProp].every((a) => a.finalizada) ? {
        texto: 'Avaliação finalizada!',
        descricao: 'A avaliação foi concluída com sucesso',
        icone: 'playlist_add_check',
        cor: 'success',
      } : {
        texto: 'Avaliação não finalizada!',
        descricao: 'É necessário finalizar a avaliação para que ela seja válida',
        icone: 'priority_high',
        cor: 'yellow',
      };
    },
  },
};
</script>
